import {Component, Input, OnInit} from '@angular/core';
import {Dad, DadSubscriberData} from '../../../models/dad';
import {FormControl, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {DadService} from '../../../services/dad.service';
import {Pop} from '../../../models/pop';
import {ThresholdExplanationComponent} from '../threshold-explanation/threshold-explanation.component';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';

@Component({
  selector: 'app-lse-report',
  templateUrl: './lse-report.component.html',
  styleUrls: ['../dad-form/dad-form.component.scss', './lse-report.component.scss']
})
export class LseReportComponent implements OnInit {

  @Input() currentDad: Dad;
  @Input() previousMonthSubscriptionData: DadSubscriberData;
  @Input() originalClientDeclarationMode = false;
  seeOriginalClientDeclaration = false;
  dadDeclarationForm: UntypedFormGroup;
  seePreviousMonthDeclaration = false;
  errors = false;
  successDeclaration = false;
  countColsArray = [];
  colsXArray = [];
  declarationError = false;
  serverError = false;
  expiredError = false;
  recentlyDeconfiguredPopsArray = [];
  loading = true;
  disabledArray = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    public dialogService: DialogService,
    private dadService: DadService) {
  }

  ngOnInit() {
    this.seeOriginalClientDeclaration = this.originalClientDeclarationMode;
    this.dadService.getRecentlyDisabledPops(this.currentDad.uniqid).subscribe(
      res => {
        this.recentlyDeconfiguredPopsArray = res;
        this.initializeMarketsArrays();
        this.createFormGroup();
      }
    );
  }

  createFormGroup() {
    this.dadDeclarationForm = this.formBuilder.group(
      {
        authorizationCheckbox: [this.isDeclared, Validators.requiredTrue],
        confirmationCheckbox: [this.isDeclared, Validators.requiredTrue],
        restrictionCheckbox: [this.isDeclared, Validators.requiredTrue],
        thresholdExplanation: [''],
      },
    );
    for (const market of this.currentDad.markets) {
        this.dadDeclarationForm.addControl(
          market,
          new FormControl({
            value: this.getFormControlValue(market),
            disabled: this.disabledArray[market]
          })
        );
    }
    this.loading = false;
  }

  private getFormControlValue(market: string) {
    if (this.disabledArray[market]) {
      return 0;
    }
    if (!this.currentDad.decodedSubscriberData || !this.currentDad.decodedSubscriberData.declarations.filter(declaration => declaration.market_name === market)[0]) {
      // Client did not declare yet.
      if (!this.previousMonthSubscriptionData || !this.previousMonthSubscriptionData.declarations || !this.previousMonthSubscriptionData.declarations.filter(declaration => declaration.market_name === market)[0]) {
        // There is no previous declaration
        return 0;
      }
      return this.previousMonthSubscriptionData.declarations.filter(declaration => declaration.market_name === market)[0].count;
    }
    return this.currentDad.decodedSubscriberData.declarations.filter(declaration => declaration.market_name === market)[0].count;
  }

  previousMonthDeclarationValue(market: string) {
    if (!this.previousMonthSubscriptionData.declarations.filter(declaration => declaration.market_name === market)[0]) {
      return 0;
    }
    return this.previousMonthSubscriptionData.declarations.filter(declaration => declaration.market_name === market)[0].count;
  }

  clientOriginalMonthDeclarationValue(market: string) {
    if (!this.currentDad.decodedOriginalClientDeclaration.declarations.filter(declaration => declaration.market_name === market)[0]) {
      return 0;
    }
    return this.currentDad.decodedOriginalClientDeclaration.declarations.filter(declaration => declaration.market_name === market)[0].count;
  }

  get previousUsageMonth() {
    if (!this.currentDad) {
      return;
    }
    const currentUsageMonth = new Date(this.currentDad.usageMonth);
    currentUsageMonth.setMonth(currentUsageMonth.getMonth() - 1);
    return currentUsageMonth;
  }

  private initializeMarketsArrays() {
    for (const market of this.currentDad.markets) {
      this.countColsArray[market] = 0;
      this.disabledArray[market] = true;
      for (const pop of this.currentDad.decodedPopList) {
        this.marketExistsForPop(market, pop);
      }
    }
  }

  marketExistsForPop(market: string, pop: Pop) {
    if (this.currentDad.dadItems[market]
      && this.currentDad.dadItems[market].length
      && this.currentDad.dadItems[market].includes(pop.display_id)) {
      if (this.recentlyDeconfiguredPopsArray[market + pop.name] === false) {
        this.countColsArray[market] += 1;
      }
      this.disabledArray[market] = false;
      this.colsXArray[market + pop.name] = 'X';
      return;
    }
    this.colsXArray[market + pop.name] = '';
  }

  toggleSeePreviousMonthDeclaration() {
    this.seePreviousMonthDeclaration = !this.seePreviousMonthDeclaration;
  }

  toggleSeeOriginalClientDeclaration() {
    this.seeOriginalClientDeclaration = !this.seeOriginalClientDeclaration;
  }

  onSubmit(withWarning = true) {
    if (withWarning && !confirm('Do you really want to submit this declaration?')) {
      return;
    }
    if (!this.dadDeclarationForm.valid) {
      this.errors = true;
      this.successDeclaration = false;
      window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
      return;
    }
    this.dadService.submitDadFormDeclaration(this.currentDad.uniqid, this.dadDeclarationForm.getRawValue()).subscribe({
        next: () => {
          this.errors = false;
          this.serverError = false;
          this.declarationError = false;
          this.successDeclaration = true;
          if (this.dadDeclarationForm.get('thresholdExplanation').value) {
            this.currentDad.largeCountDifferenceExplanation = this.dadDeclarationForm.get('thresholdExplanation').value;
          }
          this.dadDeclarationForm.get('thresholdExplanation').setValue('');
          window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
        },
        error: error => {
          if (error.status === 401) {
            this.expiredError = true;
          } else if (error.status === 406) {
            const ref = this.openThresholdExplanationPopUp();
            this.closeThresholdExplanationPopUp(ref);
            return;
          } else {
            this.serverError = true;
          }
          window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
        }
      }
    );
  }

  get previousMonthDeclarationBtnLabel() {
    if (this.seePreviousMonthDeclaration) {
      return 'Hide declared count for previous month';
    }
    return 'See declared count for previous month';
  }

  get originalClientDeclarationBtnLabel() {
    if (this.seeOriginalClientDeclaration) {
      return 'Hide the client\'s original reported count';
    }
    return 'See the client\'s original reported count';
  }

  get isDeclared() {
    return !!(this.currentDad && this.currentDad.decodedSubscriberData);
  }

  private openThresholdExplanationPopUp() {
    return this.dialogService.open(ThresholdExplanationComponent, {
      showHeader: true,
      header: 'Sudden change in declaration detected',
      width: '30%',
      style: {'max-height': '100%'},
      dismissableMask: true,
      data: {},
    });
  }

  closeThresholdExplanationPopUp(ref: DynamicDialogRef) {
    ref.onClose.subscribe(res => {
      if (!res) {
        return;
      }
      this.dadDeclarationForm.get('thresholdExplanation').setValue(res);
      this.onSubmit(false);
    });
  }
}
